import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

const LEAD_SCRIPT_KEYS = ['lead_winback_nonmember', 'lead_winback_pastmember', 'lead_trial_ready'];

export default function LeadScripts() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    get(`/scripts?club=${currentClub.id}`)
      .then(d => {
        const all = Array.isArray(d) ? d : (d?.scripts || []);
        setScripts(all.filter(s => LEAD_SCRIPT_KEYS.includes(s.script_key || s.scriptKey)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  const scriptMeta = {
    lead_winback_nonmember: { pipeline: 'Lead Engine / Win Back Non-Member', channel: 'TEXT FIRST', channelColor: '#60a5fa', channelBg: 'rgba(59,130,246,0.2)' },
    lead_winback_pastmember: { pipeline: 'Win Back Past Member', channel: 'CALL FIRST', channelColor: '#f97316', channelBg: 'rgba(234,88,12,0.2)' },
    lead_trial_ready: { pipeline: 'Trial Ready', channel: 'CALL FIRST', channelColor: '#f97316', channelBg: 'rgba(234,88,12,0.2)' },
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16, fontStyle: 'italic' }}>
        Global scripts shown. Club-level overrides take precedence when present.
      </div>

      {scripts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📝</div>
          <div className="empty-state-text">No Lead Engine scripts found.</div>
        </div>
      ) : (
        scripts.map(s => {
          const key = s.script_key || s.scriptKey;
          const meta = scriptMeta[key] || {};
          const isExpanded = expanded === key;
          const body = s.body || s.script_body || '';
          const name = s.name || s.script_name || key;
          const scope = s.scope || 'global';

          return (
            <div key={key} style={{
              background: 'var(--surface-2)', border: '1px solid var(--border)',
              borderRadius: 8, marginBottom: 10, overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }} onClick={() => setExpanded(isExpanded ? null : key)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{name}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-3)', padding: '1px 8px', borderRadius: 4 }}>{meta.pipeline || key}</span>
                    {meta.channel && (
                      <span style={{ background: meta.channelBg, color: meta.channelColor, border: `1px solid ${meta.channelColor}`, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>
                        {meta.channel}
                      </span>
                    )}
                    <span style={{
                      background: scope === 'club' ? 'rgba(234,179,8,0.2)' : 'var(--surface-3)',
                      color: scope === 'club' ? 'var(--yellow)' : 'var(--text-muted)',
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    }}>
                      {scope === 'club' ? '★ CLUB OVERRIDE' : 'GLOBAL'}
                    </span>
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Expanded body */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px' }}>
                  {s.why_context && (
                    <div style={{ background: 'var(--surface-3)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      💡 {s.why_context}
                    </div>
                  )}
                  <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
                    {body}
                  </div>
                  <button
                    style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => { navigator.clipboard?.writeText(body); }}
                  >
                    Copy Script
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
